import React from 'react';
import { Link } from 'react-router-dom';
import {
    DocPage,
    Note,
    Steps,
    Crumbs,
    WqTable,
    API_KEY_STEPS,
    API_KEYS_PATH,
    KEY_MODES,
    MERCHANT_ID_LOCATION,
} from '../../components/documentation/web-integration';

const CONTENTS = ['Your keys', 'Generate a secret key', 'Test and live keys', 'Regenerating a key'];

function ApiEnc() {
    return (
        <DocPage
            seo={{
                title: 'API Key Guide',
                description:
                    'How to generate, copy and regenerate your WayaQuick API keys for test and live mode.',
            }}
            eyebrow="Developers · Credentials"
            title={
                <>
                    WayaQuick — <em>API Key Guide</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · API Key Guide · Keep your secret key on your server."
            prev={{ to: '/test-environment', label: 'Test Environment' }}
            next={{ to: '/sdks', label: 'SDKs & Plugins' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Your keys</h2>
                <p className="wq-lead">
                    Your integration identifies your account with your Merchant ID and authenticates with your secret
                    key. Both come from your merchant dashboard.
                </p>
                <WqTable
                    columns={['Credential', 'Looks like', 'Used for']}
                    rows={[
                        [
                            <strong>Merchant ID</strong>,
                            <code>MER_...</code>,
                            `Identifies your account on every request. Shown ${MERCHANT_ID_LOCATION}.`,
                        ],
                        [
                            <strong>Secret key</strong>,
                            <code>WAYASECK_...</code>,
                            'Authenticates your server. Private — never share it or put it in browser code.',
                        ],
                        [
                            <strong>Public key</strong>,
                            '—',
                            'Used by the WayaQuick checkout page to process every payment method. Safe to expose.',
                        ],
                    ]}
                />
                <Note label="Public key">
                    When you start a payment with the API or an SDK, WayaQuick adds your public key to the checkout
                    link along with the transaction reference, so the checkout page picks it up by itself — you
                    don't send it. Only the WooCommerce plugin asks you to enter it in its settings.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Generate a secret key</h2>
                <Crumbs items={API_KEYS_PATH} />
                <Steps items={API_KEY_STEPS} />
                <Note label="Keep it secret">
                    Treat your secret key like a password. Store it in environment variables or a secret manager —
                    never in source control, a public web page or a shared document.
                </Note>
                <p className="wq-p">
                    Can't see the API Keys tab? Ask the account owner to give your team account access.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Test and live keys</h2>
                <p className="wq-p">
                    You get a separate key for each mode. The key you generate matches the mode your dashboard is in,
                    which you change with the <strong>Test / Live</strong> toggle at the top of the dashboard.
                </p>
                <WqTable columns={KEY_MODES.columns} rows={KEY_MODES.rows} />
                <p className="wq-p">
                    New accounts start in Test mode. Live mode, and with it your live key, is available once your KYC
                    has been approved —{' '}
                    <Link to="/test-environment" className="wq-link">
                        see Going live
                    </Link>
                    .
                </p>
                <Note label="The key sets the mode">
                    You don't need a separate test or live setting in your code: a test key runs test payments and a
                    live key runs real ones. To go live, swap the key. Some mobile SDKs also take an environment value —
                    set it to match the key you use.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Regenerating a key</h2>
                <p className="wq-p">
                    If you lose your secret key or think it has been exposed, generate a new one from the same API Keys
                    tab and update your integration straight away.
                </p>
                <Note label="Your old key stops working" tone="critical">
                    As soon as you generate a new secret key, the previous one stops working. Any server still using it
                    will fail until you replace it, so update every place that uses the key at the same time.
                </Note>
            </section>
        </DocPage>
    );
}

export default ApiEnc;
